import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { SandboxService } from '../../shared/services/sandbox.service';
import { SessionService } from '../session/session.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const sandbox = inject(SandboxService);
  const session = inject(SessionService);

  if (!req.url.startsWith('/api')) return next(req);

  const attach = (request: HttpRequest<unknown>, token: string) => {
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };

    // When admin is browsing the regular app in sandbox mode, override the user scope
    if (auth.getUser()?.roles.includes('admin')) {
      const sandboxId = sandbox.viewingAs();
      if (sandboxId) headers['X-Mock-User'] = sandboxId;
    }

    return request.clone({ setHeaders: headers });
  };

  const token = auth.getToken();
  if (!token) return next(req);

  return next(attach(req, token)).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) return throwError(() => err);

      // The token may have simply expired while the tab sat idle — try a silent
      // refresh before giving up and telling the user their session is dead.
      return from(auth.refreshToken()).pipe(
        switchMap(refreshed => {
          const freshToken = refreshed ? auth.getToken() : null;
          if (!freshToken) {
            session.markExpired();
            return throwError(() => err);
          }
          return next(attach(req, freshToken));
        }),
      );
    }),
  );
};
